import { LeaseRow } from '../model/lease-row';

export type PropertyTaxChangeOrigin = 'hydration' | 'user';

export type PropertyTaxDraftResult<TResponse> = {
  changeOrigin: PropertyTaxChangeOrigin;
  response: TResponse | null;
};

type PersistedPropertyTaxRowSnapshot = {
  id: string | null;
  articleId: string | null;
  fromDate: string | null;
  toDate: string | null;
  amount: number | null;
  yearRent: number | null;
  invoiceText: string | null;
  rentalObjectSize: number | null;
  leaseAgreementInvoicePeriod: number | null;
};

function createPersistedPropertyTaxRowSnapshot(row: LeaseRow): PersistedPropertyTaxRowSnapshot {
  return {
    id: row?.id ?? null,
    articleId: (row?.article as any)?.id ?? null,
    fromDate: row?.fromDate?.hasValue?.() ? row.fromDate.toString() : null,
    toDate: row?.toDate?.hasValue?.() ? row.toDate.toString() : null,
    amount: row?.amount?.amount ?? null,
    yearRent: row?.yearRent?.amount ?? null,
    invoiceText: row?.invoiceText ?? null,
    rentalObjectSize: row?.rentalObjectSize ?? null,
    leaseAgreementInvoicePeriod: row?.leaseAgreementInvoicePeriod ?? null,
  };
}

export function getPersistedPropertyTaxRowsSnapshot(rows: LeaseRow[] | undefined): string {
  return JSON.stringify((rows ?? []).map(createPersistedPropertyTaxRowSnapshot));
}

export function havePersistedPropertyTaxRowsChanged(previousSnapshot: string, rows: LeaseRow[] | undefined): boolean {
  return previousSnapshot !== getPersistedPropertyTaxRowsSnapshot(rows);
}

export function shouldIgnoreNonDraftPropertyTaxNoOp(options: {
  isDraftEditingMode: boolean;
  changeOrigin: PropertyTaxChangeOrigin;
  dirtyIntentChanged: boolean;
  currentSignatureMatchesBaseline: boolean;
  hasPendingUiMutation?: boolean;
}): boolean {
  if (options.isDraftEditingMode) return false;
  if (options.changeOrigin === 'hydration') return true;
  if (options.hasPendingUiMutation) return false;

  return !options.dirtyIntentChanged && options.currentSignatureMatchesBaseline;
}

export class PropertyTaxFormState {
  private _pendingDraftChangeOrigin: PropertyTaxChangeOrigin = 'hydration';
  private _dirtyBaselineSnapshot = '';

  get pendingDraftChangeOrigin(): PropertyTaxChangeOrigin {
    return this._pendingDraftChangeOrigin;
  }

  setPendingDraftChangeOrigin(changeOrigin: PropertyTaxChangeOrigin = 'user'): void {
    this._pendingDraftChangeOrigin = changeOrigin;
  }

  refreshDirtyBaseline(snapshot: string): void {
    this._dirtyBaselineSnapshot = snapshot;
  }

  hasDirtyIntentChanged(currentSnapshot: string): boolean {
    return this._dirtyBaselineSnapshot !== currentSnapshot;
  }

  shouldMarkDirty(changeOrigin: PropertyTaxChangeOrigin, markDirty: boolean, currentSnapshot: string): boolean {
    return changeOrigin === 'user' && markDirty && this.hasDirtyIntentChanged(currentSnapshot);
  }
}
